"use client";
// @ts-strict-ignore
import { Body } from "components/parts/Typography";
import * as React from "react";
import {
  BlogPostContainer,
  PostAuthorDetails,
  PostAuthorLink,
  PostDetailsContainer,
  PostDetailsInner,
  PostImgContainer,
  PostIntro,
  PostTitleLink
} from "./style";
import NextLink from "components/parts/NextLink";
import Image from "next/image";
import { contentfulLoader } from "utils/images/contentfulLoader";
import type { PostPropType } from "types/blog-type";
import { format } from "date-fns";
import { useRouter } from "i18n/routing";

interface BlogPostProps {
  post: PostPropType;
  displayIntro?: boolean;
  displayAuthorDetails?: boolean;
  postMetaDetailsPosition?: "top" | "bottom";
  containerStyles?: React.CSSProperties;
  openPostInNewTab?: boolean;
  postMetaDetailsContainerStyles?: React.CSSProperties;
  authorDetailsStyles?: React.CSSProperties;
  introductionStyles?: React.CSSProperties;
  titleStyles?: React.CSSProperties;
}

const BlogPost = ({
  post,
  displayIntro = true,
  displayAuthorDetails = true,
  postMetaDetailsPosition = "top",
  containerStyles = {},
  openPostInNewTab = false,
  postMetaDetailsContainerStyles = {},
  authorDetailsStyles = {},
  introductionStyles = {},
  titleStyles = {}
}: BlogPostProps) => {
  const {
    title,
    introduction,
    datePublished,
    articleType,
    articleAuthor,
    postImage,
    postImageAlt,
    authorPosition,
    postSlug,
    authorSlug
  } = post;
  const router = useRouter();
  const [activeElement, setActiveElement] = React.useState(null);
  const buildPostUrl = React.useCallback(() => {
    // considering articleType is only defined for BLOG posts
    return articleType ? `/blog/${postSlug}` : `/cryptopedia/${postSlug}`;
  }, [postSlug, articleType]);

  const handleAuthorHover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveElement("author");
  };

  const handleContainerHover = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setActiveElement("container");
  };

  const handleAuthorClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (openPostInNewTab) {
      window.open(
        `/${articleType ? "blog/author/" : "cryptopedia/authors/#"}${authorSlug}`,
        "_blank"
      );
    } else {
      router.push(
        `/${articleType ? "blog/author/" : "cryptopedia/authors/#"}${authorSlug}`
      );
    }
  };

  const handleMouseOut = (e) => {
    e.preventDefault();
    setActiveElement(null);
  };
  const handleBlogPostClick = (event) => {
    event.preventDefault();
    router.push(buildPostUrl());
  };

  return (
    <BlogPostContainer
      data-testid="blog-item"
      onMouseOver={(e) => handleContainerHover(e)}
      onMouseOut={(e) => handleMouseOut(e)}
      active={activeElement !== "container"}
      onClick={handleBlogPostClick}
      style={containerStyles}
    >
      <PostImgContainer>
        <Image
          src={postImage}
          alt={postImageAlt}
          width={345}
          height={195}
          loader={contentfulLoader}
        />
      </PostImgContainer>
      <PostDetailsContainer>
        <div>
          {postMetaDetailsPosition === "top" && (
            <PostDetailsInner style={postMetaDetailsContainerStyles}>
              <Body size="sm" m="0" mr="8px" color={"secondary"}>
                {articleType?.toUpperCase()}
              </Body>
              <Body size="sm" m="0" color={"secondary"}>
                {format(new Date(datePublished), "MMM dd, yyyy").toUpperCase()}
              </Body>
            </PostDetailsInner>
          )}
          <NextLink href={buildPostUrl()}>
            <PostTitleLink
              title={title}
              m="8px 0 4px"
              forwardedAs="h4"
              style={titleStyles}
            >
              {title}
            </PostTitleLink>
          </NextLink>
          {displayIntro && (
            <PostIntro
              applyEllpisis={true}
              style={introductionStyles}
              size="sm"
              mt="0"
              color={"secondary"}
              textAlign="left"
              title={introduction}
            >
              {introduction}
            </PostIntro>
          )}{" "}
          {postMetaDetailsPosition === "bottom" && (
            <PostDetailsInner style={postMetaDetailsContainerStyles}>
              <Body size="sm" m="0" mr="8px" color={"secondary"}>
                {articleType?.toUpperCase()}
              </Body>
              <Body size="sm" m="0" color={"secondary"}>
                {format(new Date(datePublished), "MMM dd, yyyy").toUpperCase()}
              </Body>
            </PostDetailsInner>
          )}
        </div>
        {displayAuthorDetails && (
          <PostAuthorDetails
            onMouseOver={(e) => handleAuthorHover(e)}
            onMouseOut={(e) => handleMouseOut(e)}
            onClick={(e) => handleAuthorClick(e)}
            active={activeElement === "author"}
            style={authorDetailsStyles}
          >
            <PostAuthorLink m="0" color={"secondary"} semiBold>
              {articleAuthor?.toUpperCase()}
            </PostAuthorLink>
            <Body size="sm" m="0" color={"secondary"}>
              {authorPosition}
            </Body>
          </PostAuthorDetails>
        )}
      </PostDetailsContainer>
    </BlogPostContainer>
  );
};

export default BlogPost;
